// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';
import { useFuel } from '../contexts/FuelContext';
import { useUpgrades } from '../contexts/UpgradesContext';

const ShipVisualization = ({ 
  systems = [], 
  currentAlert, 
  rotation = 0, 
  sparks = [], 
  onSystemClick 
}) => {
  const { tankUpgrades } = useFuel();
  const { hasUpgrade } = useUpgrades();
  
  // Check for installed upgrades (any tier)
  const hasShieldUpgrade = hasUpgrade('shield_1') || 
                           hasUpgrade('shield_2') ||
                           hasUpgrade('shield_3');
  const hasEngineUpgrade = hasUpgrade('engine_1') || 
                           hasUpgrade('engine_2') ||
                           hasUpgrade('engine_3');
  const hasWeaponUpgrade = hasUpgrade('weapon_1') || 
                           hasUpgrade('weapon_2') ||
                           hasUpgrade('weapon_3');
  const hasPowerUpgrade = hasUpgrade('special_1') || 
                          hasUpgrade('special_2') ||
                          hasUpgrade('special_3');
  
  return (
    <>
      {/* System Alert Banner - only show if currentAlert exists */}
      {currentAlert && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(0, 0, 0, 0.8)',
          border: '1px solid #00ffff',
          borderRadius: '5px',
          padding: '8px 20px',
          color: currentAlert?.severity === 'WARNING' ? '#ffaa00' : 
                 currentAlert?.severity === 'CAUTION' ? '#ffaa00' : '#00ff88',
          fontSize: '0.9em',
          fontWeight: 'bold',
          zIndex: 100,
          boxShadow: currentAlert?.severity === 'WARNING' || currentAlert?.severity === 'CAUTION'
            ? '0 0 15px rgba(255, 170, 0, 0.5)' 
            : '0 0 15px rgba(0, 255, 136, 0.5)',
          animation: 'alertFadeIn 0.5s ease-out',
          textAlign: 'center',
          minWidth: '300px'
        }}>
          {currentAlert?.severity === 'WARNING' ? '⚠️' : 
           currentAlert?.severity === 'CAUTION' ? '🔧' : '✓'} {currentAlert?.message || 'ALL SYSTEMS NOMINAL'}
        </div>
      )}
      
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%) rotate(90deg) rotateY(${rotation}deg) scaleY(2.2)`,
        transformStyle: 'preserve-3d',
        width: '400px',
        height: '400px',
        pointerEvents: 'none'
      }}>
        {/* Ship Outline */}
        <svg width="400" height="400" viewBox="0 0 400 400" style={{ 
          filter: 'drop-shadow(0 0 20px #00ffff) drop-shadow(0 0 40px rgba(0, 255, 255, 0.5))',
          transition: 'filter 0.3s ease',
          pointerEvents: 'none'
        }}>
          {/* Glow effect layer */}
          <defs>
            <radialGradient id="saucerGlow" cx="50%" cy="50%">
              <stop offset="0%" stopColor="#00ffff" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#00ffff" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="engineGlow" cx="50%" cy="50%">
              <stop offset="0%" stopColor="#ff00ff" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#ff00ff" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="engineUpgradeGlow" cx="50%" cy="50%">
              <stop offset="0%" stopColor="#00ffff" stopOpacity="0.9" />
              <stop offset="50%" stopColor="#ff00ff" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#ff00ff" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="hullGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#00ffff" stopOpacity="0.3" />
              <stop offset="50%" stopColor="#00ffff" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#00ffff" stopOpacity="0.3" />
            </linearGradient>
            <radialGradient id="shieldGradient" cx="50%" cy="50%">
              <stop offset="0%" stopColor="#00ffff" stopOpacity="0" />
              <stop offset="70%" stopColor="#00ffff" stopOpacity="0.15" />
              <stop offset="85%" stopColor="#00ffff" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#00ffff" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="weaponGlow" cx="50%" cy="50%">
              <stop offset="0%" stopColor="#ff4444" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#ff4444" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="powerCoreGlow" cx="50%" cy="50%">
              <stop offset="0%" stopColor="#ffff00" stopOpacity="0.8" />
              <stop offset="50%" stopColor="#ffaa00" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#ffaa00" stopOpacity="0" />
            </radialGradient>
          </defs>
          
          {/* Shield Bubble - Only if shield upgrade installed */}
          {hasShieldUpgrade && (
            <>
              <ellipse cx="200" cy="240" rx="160" ry="180" fill="url(#shieldGradient)" opacity="0.8">
                <animate attributeName="opacity" values="0.6;0.9;0.6" dur="3s" repeatCount="indefinite"/>
              </ellipse>
              <ellipse cx="200" cy="240" rx="160" ry="180" fill="none" stroke="#00ffff" strokeWidth="2" opacity="0.5">
                <animate attributeName="opacity" values="0.3;0.7;0.3" dur="3s" repeatCount="indefinite"/>
                <animate attributeName="rx" values="160;165;160" dur="3s" repeatCount="indefinite"/>
                <animate attributeName="ry" values="180;185;180" dur="3s" repeatCount="indefinite"/>
              </ellipse>
              {/* Shield shimmer effect */}
              <ellipse cx="200" cy="240" rx="155" ry="175" fill="none" stroke="#ffffff" strokeWidth="1" opacity="0.3">
                <animate attributeName="opacity" values="0;0.5;0" dur="2s" repeatCount="indefinite"/>
              </ellipse>
            </>
          )}
          
          {/* Main Saucer with glow */}
          <ellipse cx="200" cy="150" rx="125" ry="65" fill="url(#saucerGlow)" opacity="0.5"/>
          <ellipse cx="200" cy="150" rx="120" ry="60" fill="url(#hullGradient)" stroke="#00ffff" strokeWidth="2"/>
          
          {/* Saucer detail lines */}
          <ellipse cx="200" cy="150" rx="100" ry="50" fill="none" stroke="#00ffff" strokeWidth="1" opacity="0.5"/>
          <ellipse cx="200" cy="150" rx="80" ry="40" fill="none" stroke="#00ffff" strokeWidth="1" opacity="0.3"/>
          
          {/* Bridge dome */}
          <ellipse cx="200" cy="140" rx="40" ry="20" fill="rgba(0, 255, 255, 0.3)" stroke="#00ffff" strokeWidth="1.5"/>
          
          {/* Neck */}
          <rect x="190" y="180" width="20" height="60" fill="url(#hullGradient)" stroke="#00ffff" strokeWidth="2"/>
          
          {/* Engineering Hull with glow */}
          <ellipse cx="200" cy="280" rx="65" ry="85" fill="url(#engineGlow)" opacity="0.4"/>
          <ellipse cx="200" cy="280" rx="60" ry="80" fill="url(#hullGradient)" stroke="#00ffff" strokeWidth="2"/>
          
          {/* Base Fuel Tank (always visible) */}
          <rect x="185" y="260" width="30" height="40" rx="5" fill="rgba(0, 255, 136, 0.3)" stroke="#00ff88" strokeWidth="2"/>
          <text x="200" y="283" textAnchor="middle" fill="#00ff88" fontSize="10" fontWeight="bold">FUEL</text>
          
          {/* Additional Fuel Tanks (based on upgrades) */}
          {tankUpgrades >= 1 && (
            <>
              <rect x="165" y="265" width="15" height="30" rx="3" fill="rgba(0, 255, 255, 0.4)" stroke="#00ffff" strokeWidth="1.5"/>
              <text x="172.5" y="283" textAnchor="middle" fill="#00ffff" fontSize="8">+1</text>
            </>
          )}
          {tankUpgrades >= 2 && (
            <>
              <rect x="220" y="265" width="15" height="30" rx="3" fill="rgba(0, 255, 255, 0.4)" stroke="#00ffff" strokeWidth="1.5"/>
              <text x="227.5" y="283" textAnchor="middle" fill="#00ffff" fontSize="8">+2</text>
            </>
          )}
          {tankUpgrades >= 3 && (
            <>
              <rect x="170" y="300" width="15" height="25" rx="3" fill="rgba(0, 255, 255, 0.4)" stroke="#00ffff" strokeWidth="1.5"/>
              <text x="177.5" y="315" textAnchor="middle" fill="#00ffff" fontSize="8">+3</text>
            </>
          )}
          {tankUpgrades >= 4 && (
            <>
              <rect x="215" y="300" width="15" height="25" rx="3" fill="rgba(0, 255, 255, 0.4)" stroke="#00ffff" strokeWidth="1.5"/>
              <text x="222.5" y="315" textAnchor="middle" fill="#00ffff" fontSize="8">+4</text>
            </>
          )}
          
          {/* Nacelles with glow */}
          <rect x="100" y="250" width="40" height="100" rx="10" fill="url(#engineGlow)" opacity="0.5"/>
          <rect x="100" y="250" width="40" height="100" rx="10" fill="url(#hullGradient)" stroke="#00ffff" strokeWidth="2"/>
          <rect x="260" y="250" width="40" height="100" rx="10" fill="url(#engineGlow)" opacity="0.5"/>
          <rect x="260" y="250" width="40" height="100" rx="10" fill="url(#hullGradient)" stroke="#00ffff" strokeWidth="2"/>
          
          {/* Nacelle detail lines */}
          <line x1="110" y1="260" x2="110" y2="340" stroke="#ff00ff" strokeWidth="1" opacity="0.7"/>
          <line x1="130" y1="260" x2="130" y2="340" stroke="#ff00ff" strokeWidth="1" opacity="0.7"/>
          <line x1="270" y1="260" x2="270" y2="340" stroke="#ff00ff" strokeWidth="1" opacity="0.7"/>
          <line x1="290" y1="260" x2="290" y2="340" stroke="#ff00ff" strokeWidth="1" opacity="0.7"/>
          
          {/* Warp coils (glowing) */}
          <rect x="105" y="270" width="30" height="8" rx="4" fill={hasEngineUpgrade ? "#00ffff" : "#ff00ff"} opacity="0.8">
            <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite"/>
          </rect>
          <rect x="105" y="300" width="30" height="8" rx="4" fill={hasEngineUpgrade ? "#00ffff" : "#ff00ff"} opacity="0.8">
            <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" begin="0.5s" repeatCount="indefinite"/>
          </rect>
          <rect x="105" y="330" width="30" height="8" rx="4" fill={hasEngineUpgrade ? "#00ffff" : "#ff00ff"} opacity="0.8">
            <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" begin="1s" repeatCount="indefinite"/>
          </rect>
          <rect x="265" y="270" width="30" height="8" rx="4" fill={hasEngineUpgrade ? "#00ffff" : "#ff00ff"} opacity="0.8">
            <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" repeatCount="indefinite"/>
          </rect>
          <rect x="265" y="300" width="30" height="8" rx="4" fill={hasEngineUpgrade ? "#00ffff" : "#ff00ff"} opacity="0.8">
            <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" begin="0.5s" repeatCount="indefinite"/>
          </rect>
          <rect x="265" y="330" width="30" height="8" rx="4" fill={hasEngineUpgrade ? "#00ffff" : "#ff00ff"} opacity="0.8">
            <animate attributeName="opacity" values="0.8;1;0.8" dur="2s" begin="1s" repeatCount="indefinite"/>
          </rect>
          
          {/* Enhanced Engine Glow - Only if engine upgrade installed */}
          {hasEngineUpgrade && (
            <>
              {/* Extra bright nacelle glow */}
              <ellipse cx="120" cy="300" rx="25" ry="60" fill="url(#engineUpgradeGlow)" opacity="0.6">
                <animate attributeName="opacity" values="0.4;0.8;0.4" dur="1.5s" repeatCount="indefinite"/>
              </ellipse>
              <ellipse cx="280" cy="300" rx="25" ry="60" fill="url(#engineUpgradeGlow)" opacity="0.6">
                <animate attributeName="opacity" values="0.4;0.8;0.4" dur="1.5s" begin="0.75s" repeatCount="indefinite"/>
              </ellipse>
            </>
          )}
          
          {/* Engine Thrust Glow - Pulsing at back of nacelles */}
          <ellipse cx="120" cy="355" rx={hasEngineUpgrade ? "20" : "15"} ry={hasEngineUpgrade ? "30" : "25"} fill="#00ffff" opacity="0.6">
            <animate attributeName="opacity" values="0.3;0.8;0.3" dur={hasEngineUpgrade ? "0.7s" : "1s"} repeatCount="indefinite"/>
            <animate attributeName="ry" values={hasEngineUpgrade ? "30;40;30" : "25;30;25"} dur={hasEngineUpgrade ? "0.7s" : "1s"} repeatCount="indefinite"/>
          </ellipse>
          <ellipse cx="120" cy="355" rx={hasEngineUpgrade ? "15" : "10"} ry={hasEngineUpgrade ? "25" : "20"} fill="#ffffff" opacity="0.8">
            <animate attributeName="opacity" values="0.5;1;0.5" dur={hasEngineUpgrade ? "0.7s" : "1s"} repeatCount="indefinite"/>
          </ellipse>
          
          <ellipse cx="280" cy="355" rx={hasEngineUpgrade ? "20" : "15"} ry={hasEngineUpgrade ? "30" : "25"} fill="#00ffff" opacity="0.6">
            <animate attributeName="opacity" values="0.3;0.8;0.3" dur={hasEngineUpgrade ? "0.7s" : "1s"} begin="0.5s" repeatCount="indefinite"/>
            <animate attributeName="ry" values={hasEngineUpgrade ? "30;40;30" : "25;30;25"} dur={hasEngineUpgrade ? "0.7s" : "1s"} begin="0.5s" repeatCount="indefinite"/>
          </ellipse>
          <ellipse cx="280" cy="355" rx={hasEngineUpgrade ? "15" : "10"} ry={hasEngineUpgrade ? "25" : "20"} fill="#ffffff" opacity="0.8">
            <animate attributeName="opacity" values="0.5;1;0.5" dur={hasEngineUpgrade ? "0.7s" : "1s"} begin="0.5s" repeatCount="indefinite"/>
          </ellipse>
          
          {/* Impulse Engine Glow - Main hull */}
          <ellipse cx="200" cy="365" rx={hasPowerUpgrade ? "25" : "20"} ry={hasPowerUpgrade ? "20" : "15"} fill={hasPowerUpgrade ? "url(#powerCoreGlow)" : "#ff00ff"} opacity="0.5">
            <animate attributeName="opacity" values="0.3;0.7;0.3" dur="1.5s" repeatCount="indefinite"/>
            <animate attributeName="ry" values={hasPowerUpgrade ? "20;25;20" : "15;20;15"} dur="1.5s" repeatCount="indefinite"/>
          </ellipse>
          <ellipse cx="200" cy="365" rx={hasPowerUpgrade ? "15" : "12"} ry={hasPowerUpgrade ? "15" : "10"} fill="#ffffff" opacity="0.7">
            <animate attributeName="opacity" values="0.4;0.9;0.4" dur="1.5s" repeatCount="indefinite"/>
          </ellipse>
          
          {/* Power Core Glow - Only if power upgrade installed */}
          {hasPowerUpgrade && (
            <>
              <ellipse cx="200" cy="280" rx="40" ry="50" fill="url(#powerCoreGlow)" opacity="0.5">
                <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite"/>
              </ellipse>
              <circle cx="200" cy="280" r="15" fill="#ffff00" opacity="0.6">
                <animate attributeName="opacity" values="0.4;0.8;0.4" dur="1s" repeatCount="indefinite"/>
                <animate attributeName="r" values="15;18;15" dur="1s" repeatCount="indefinite"/>
              </circle>
            </>
          )}
          
          {/* Weapon Hardpoints - Only if weapon upgrade installed */}
          {hasWeaponUpgrade && (
            <>
              {/* Forward torpedo bay */}
              <rect x="190" y="195" width="20" height="8" rx="2" fill="url(#weaponGlow)" opacity="0.7">
                <animate attributeName="opacity" values="0.5;0.9;0.5" dur="1.5s" repeatCount="indefinite"/>
              </rect>
              <circle cx="200" cy="199" r="3" fill="#ff4444" opacity="0.9">
                <animate attributeName="opacity" values="0.6;1;0.6" dur="1s" repeatCount="indefinite"/>
              </circle>
              
              {/* Phaser arrays on saucer */}
              <rect x="130" y="145" width="15" height="4" rx="2" fill="#ff4444" opacity="0.6">
                <animate attributeName="opacity" values="0.4;0.8;0.4" dur="1.2s" repeatCount="indefinite"/>
              </rect>
              <rect x="255" y="145" width="15" height="4" rx="2" fill="#ff4444" opacity="0.6">
                <animate attributeName="opacity" values="0.4;0.8;0.4" dur="1.2s" begin="0.6s" repeatCount="indefinite"/>
              </rect>
              
              {/* Aft weapons */}
              <circle cx="200" cy="350" r="5" fill="#ff4444" opacity="0.7">
                <animate attributeName="opacity" values="0.5;0.9;0.5" dur="1.3s" repeatCount="indefinite"/>
              </circle>
            </>
          )}
          
          {/* Pylons with detail */}
          <line x1="160" y1="280" x2="120" y2="280" stroke="#00ffff" strokeWidth="3"/>
          <line x1="240" y1="280" x2="280" y2="280" stroke="#00ffff" strokeWidth="3"/>
          <line x1="160" y1="275" x2="120" y2="275" stroke="#00ffff" strokeWidth="1" opacity="0.5"/>
          <line x1="160" y1="285" x2="120" y2="285" stroke="#00ffff" strokeWidth="1" opacity="0.5"/>
          <line x1="240" y1="275" x2="280" y2="275" stroke="#00ffff" strokeWidth="1" opacity="0.5"/>
          <line x1="240" y1="285" x2="280" y2="285" stroke="#00ffff" strokeWidth="1" opacity="0.5"/>
        </svg>
        
        {/* System Damage Indicators */}
        {systems.map(system => (
          <div
            key={system.id}
            onClick={() => onSystemClick && onSystemClick(system.id)}
            style={{
              position: 'absolute',
              left: `${system.x}%`,
              top: `${system.y}%`,
              transform: 'translate(-50%, -50%)',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: system.damaged 
                ? 'radial-gradient(circle, rgba(255, 68, 68, 0.8), rgba(255, 0, 0, 0.3))'
                : system.repairing
                ? 'radial-gradient(circle, rgba(255, 170, 0, 0.8), rgba(255, 170, 0, 0.3))'
                : `radial-gradient(circle, rgba(0, 255, 136, 0.6), transparent)`,
              border: system.damaged ? '2px solid #ff4444' : system.repairing ? '2px solid #ffaa00' : '2px solid #00ff88',
              boxShadow: system.damaged 
                ? '0 0 20px #ff4444' 
                : system.repairing
                ? '0 0 20px #ffaa00'
                : '0 0 10px #00ff88',
              cursor: 'pointer',
              animation: system.damaged ? 'pulse 1s ease-in-out infinite' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.2em',
              zIndex: 1000,
              pointerEvents: 'auto', // Enable clicks on indicators
              transition: 'transform 0.2s ease, box-shadow 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.2)';
              e.currentTarget.style.boxShadow = system.damaged 
                ? '0 0 30px #ff4444' 
                : '0 0 20px #00ff88';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(-50%, -50%)';
              e.currentTarget.style.boxShadow = system.damaged 
                ? '0 0 20px #ff4444' 
                : '0 0 10px #00ff88';
            }}
            title={system.name}
          >
            {system.damaged ? '💥' : system.repairing ? '🔧' : '✓'}
          </div>
        ))}
        
        {/* Sparks */}
        {sparks.map(spark => (
          <div
            key={spark.id}
            style={{
              position: 'absolute',
              left: `${spark.x}%`,
              top: `${spark.y}%`,
              transform: 'translate(-50%, -50%)',
              fontSize: '2em',
              animation: 'sparkle 0.5s ease-in-out infinite',
              pointerEvents: 'none'
            }}
          >
            ⚡
          </div>
        ))}
      </div>
    </>
  );
};

export default ShipVisualization;
